import React, { Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PermissionsProvider, usePermissions } from "@/contexts/PermissionsContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PharmacyModeProvider } from '@/contexts/PharmacyModeContext';

// Eagerly loaded (critical path)
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import MobileLayout from "@/components/MobileLayout";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import OnboardingPage from "@/pages/OnboardingPage";
import WorkspacePendingPage from "@/pages/WorkspacePendingPage";

// Lazy loaded (route-based code splitting)
const AptekaControlPage = React.lazy(() => import("@/pages/AptekaControlPage"));
const CatalogPage = React.lazy(() => import("@/pages/CatalogPage"));
const MonthClosingPage = React.lazy(() => import("@/pages/MonthClosingPage"));
const MonthOpeningWizardPage = React.lazy(() => import("@/pages/MonthOpeningWizardPage"));
const MRDashboardPage = React.lazy(() => import("@/pages/MRDashboardPage"));
const DeliveriesPage = React.lazy(() => import("@/pages/DeliveriesPage"));
const PaymentsPage = React.lazy(() => import("@/pages/PaymentsPage"));
const PaymentCalendarPage = React.lazy(() => import("@/pages/PaymentCalendarPage"));
const ProductsPage = React.lazy(() => import("@/pages/ProductsPage"));
const ComingSoonPage = React.lazy(() => import("@/pages/ComingSoonPage"));
const InventoryRiskPage = React.lazy(() => import("@/pages/InventoryRiskPage"));
const SequenceAuditPage = React.lazy(() => import("@/pages/SequenceAuditPage"));
const TelegramAuditPage = React.lazy(() => import("@/pages/TelegramAuditPage"));
const DistrictSummaryPage = React.lazy(() => import("@/pages/DistrictSummaryPage"));
const GrowthPage = React.lazy(() => import("@/pages/GrowthPage"));
const SettingsPage = React.lazy(() => import("@/pages/SettingsPage"));
const WarehousePage = React.lazy(() => import("@/pages/WarehousePage"));
const DiaryPage = React.lazy(() => import("@/pages/DiaryPage"));
const CompanyCatalogPage = React.lazy(() => import("@/pages/CompanyCatalogPage"));
const BulkImportPage = React.lazy(() => import("@/pages/BulkImportPage"));
const NotificationsPage = React.lazy(() => import("@/pages/NotificationsPage"));
const MessagesPage = React.lazy(() => import("@/pages/MessagesPage"));
const ShareCodeAnalyticsPage = React.lazy(() => import("@/pages/ShareCodeAnalyticsPage"));
const AptekaCatalogPage = React.lazy(() => import("@/pages/AptekaCatalogPage"));
const AuditLogPage = React.lazy(() => import("@/pages/AuditLogPage"));
const PharmacyDashboardPage = React.lazy(() => import("@/pages/PharmacyDashboardPage"));
const ProductIntelligencePage = React.lazy(() => import("@/pages/ProductIntelligencePage"));
const ReliabilityScoresPage = React.lazy(() => import("@/pages/ReliabilityScoresPage"));
const ExceptionDashboardPage = React.lazy(() => import("@/pages/ExceptionDashboardPage"));
const ControlRoomPage = React.lazy(() => import("@/pages/control-room/ControlRoomPage"));
const ConnectionsPage = React.lazy(() => import("@/pages/ConnectionsPage"));
const SubscriptionPage = React.lazy(() => import("@/pages/SubscriptionPage"));
const SubscriptionSuccessPage = React.lazy(() => import("@/pages/SubscriptionSuccessPage"));
const ResetPasswordPage = React.lazy(() => import("@/pages/ResetPasswordPage"));
const InviteSetupPage = React.lazy(() => import("@/pages/InviteSetupPage"));
const MRMobilePage = React.lazy(() => import("@/pages/MRMobilePage"));
const HelpPage = React.lazy(() => import("@/pages/HelpPage"));
const LedgerPage = React.lazy(() => import("@/pages/LedgerPage"));
const ReturnsPage = React.lazy(() => import("@/pages/ReturnsPage"));
const OrdersPage = React.lazy(() => import("@/pages/OrdersPage"));
const LowStockPage = React.lazy(() => import("@/pages/LowStockPage"));
const AIInsightsPage = React.lazy(() => import("@/pages/AIInsightsPage"));
const BrainPage = React.lazy(() => import("@/pages/BrainPage"));
const AutopilotPage = React.lazy(() => import("@/pages/AutopilotPage"));
const WarehouseOrdersPage = React.lazy(() => import("@/pages/WarehouseOrdersPage"));
const PurchaseOrdersPage = React.lazy(() => import("@/pages/PurchaseOrdersPage"));

function ChunkFallback() {
  return (
    <div className="p-4 space-y-3 animate-pulse">
      <div className="h-8 w-48 bg-muted rounded-lg" />
      <div className="h-4 w-full bg-muted rounded" />
      <div className="h-4 w-3/4 bg-muted rounded" />
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="h-20 bg-muted rounded-xl" />
        <div className="h-20 bg-muted rounded-xl" />
        <div className="h-20 bg-muted rounded-xl" />
      </div>
      <div className="h-40 bg-muted rounded-xl mt-2" />
    </div>
  );
}

function Lazy({ children }) {
  return <Suspense fallback={<ChunkFallback />}>{children}</Suspense>;
}

function useIsMobile(breakpoint = 1024) {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < breakpoint);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

function LayoutSwitcher() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isPharmacy = ['apteka', 'pharmacy_owner', 'pharmacy_admin'].includes(user?.role);
  if (isMobile && isPharmacy) return <MobileLayout />;
  return <Layout />;
}

function ProtectedRoute({ children }) {
  const { user, loading, needsOnboarding, isPlatformUser } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/home" replace />;
  if (isPlatformUser) return children;
  if (user.role === 'mr') return children;
  if (user.role === 'pending' || (!user.active_workspace_id && !user.workspace_id)) {
    return <OnboardingPage />;
  }
  if (user.workspace_status === 'pending' || user.workspace_status === 'blocked') {
    return <WorkspacePendingPage />;
  }
  return children;
}

function RBACRoute({ permKey, children }) {
  const { hasAccess } = usePermissions();
  const { user } = useAuth();
  if (['platform_owner', 'platform_admin', 'support_admin'].includes(user?._realRole || user?.role)) return children;
  if (!hasAccess(permKey)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  const isPlatform = ['platform_owner', 'platform_admin', 'support_admin'].includes(user?._realRole || user?.role);
  const isPharmacy = ['apteka', 'pharmacy_owner', 'pharmacy_admin'].includes(user?.role);
  if (isPlatform) return <Navigate to="/internal/control-room" replace />;
  if (user?.role === 'mr') return <Navigate to="/mr-mobile" replace />;
  if (isPharmacy) return <Navigate to="/pharmacy-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function DashboardRedirect() {
  const { user } = useAuth();
  const isPharmacy = ['apteka', 'pharmacy_owner', 'pharmacy_admin'].includes(user?.role);
  if (isPharmacy) return <Navigate to="/pharmacy-dashboard" replace />;
  return <ErrorBoundary><DashboardPage /></ErrorBoundary>;
}

function PlatformRoute({ children }) {
  const { user } = useAuth();
  const isPlatform = ['platform_owner', 'platform_admin', 'support_admin'].includes(user?._realRole || user?.role);
  if (!isPlatform) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/home" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<Lazy><ResetPasswordPage /></Lazy>} />
      <Route path="/invite/:token" element={<Lazy><InviteSetupPage /></Lazy>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/mr-mobile" element={<ProtectedRoute><Lazy><ErrorBoundary><MRMobilePage /></ErrorBoundary></Lazy></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><LayoutSwitcher /></ProtectedRoute>}>
        <Route index element={<DefaultRedirect />} />
        <Route path="dashboard" element={<DashboardRedirect />} />
        <Route path="apteka-control" element={<RBACRoute permKey="apteka_control"><Lazy><ErrorBoundary><AptekaControlPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="deliveries" element={<RBACRoute permKey="deliveries"><Lazy><ErrorBoundary><DeliveriesPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="payments" element={<RBACRoute permKey="payments"><Lazy><ErrorBoundary><PaymentsPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="payment-calendar" element={<RBACRoute permKey="payments"><Lazy><ErrorBoundary><PaymentCalendarPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="ledger" element={<Lazy><ErrorBoundary><LedgerPage /></ErrorBoundary></Lazy>} />
        <Route path="returns" element={<Lazy><ErrorBoundary><ReturnsPage /></ErrorBoundary></Lazy>} />
        <Route path="orders" element={<Lazy><ErrorBoundary><OrdersPage /></ErrorBoundary></Lazy>} />
        <Route path="warehouse/orders" element={<RBACRoute permKey="warehouse"><Lazy><ErrorBoundary><WarehouseOrdersPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="purchase-orders" element={<RBACRoute permKey="warehouse"><Lazy><ErrorBoundary><PurchaseOrdersPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="low-stock" element={<Lazy><ErrorBoundary><LowStockPage /></ErrorBoundary></Lazy>} />
        <Route path="products" element={<RBACRoute permKey="products"><Lazy><ErrorBoundary><ProductsPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="inventory-risk" element={<RBACRoute permKey="inventory"><Lazy><ErrorBoundary><InventoryRiskPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="sequence-audit" element={<RBACRoute permKey="inventory"><Lazy><ErrorBoundary><SequenceAuditPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="telegram-audit" element={<RBACRoute permKey="apteka_control"><Lazy><ErrorBoundary><TelegramAuditPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="catalog" element={<RBACRoute permKey="catalog"><Lazy><ErrorBoundary><CatalogPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="catalog/bulk-import" element={<Lazy><ErrorBoundary><BulkImportPage /></ErrorBoundary></Lazy>} />
        <Route path="warehouse" element={<RBACRoute permKey="warehouse"><Lazy><ErrorBoundary><WarehousePage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="district-summary" element={<RBACRoute permKey="district_summary"><Lazy><ErrorBoundary><DistrictSummaryPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="growth" element={<RBACRoute permKey="growth"><Lazy><ErrorBoundary><GrowthPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="month-closing" element={<RBACRoute permKey="month_closing"><Lazy><ErrorBoundary><MonthClosingPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="month-opening" element={<RBACRoute permKey="month_closing"><Lazy><ErrorBoundary><MonthOpeningWizardPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="mr-dashboard" element={<RBACRoute permKey="mr_dashboard"><Lazy><ErrorBoundary><MRDashboardPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="diary" element={<RBACRoute permKey="diary"><Lazy><ErrorBoundary><DiaryPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="company-catalog" element={<RBACRoute permKey="company_catalog"><Lazy><ErrorBoundary><CompanyCatalogPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="settings" element={<RBACRoute permKey="settings"><Lazy><ErrorBoundary><SettingsPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="notifications" element={<Lazy><ErrorBoundary><NotificationsPage /></ErrorBoundary></Lazy>} />
        <Route path="messages" element={<Lazy><ErrorBoundary><MessagesPage /></ErrorBoundary></Lazy>} />
        <Route path="share-code-analytics" element={<RBACRoute permKey="share_code_analytics"><Lazy><ErrorBoundary><ShareCodeAnalyticsPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="apteka-catalog" element={<Lazy><ErrorBoundary><AptekaCatalogPage /></ErrorBoundary></Lazy>} />
        <Route path="audit-log" element={<RBACRoute permKey="settings"><Lazy><ErrorBoundary><AuditLogPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="pharmacy-dashboard" element={<Lazy><ErrorBoundary><PharmacyDashboardPage /></ErrorBoundary></Lazy>} />
        <Route path="product-intelligence" element={<RBACRoute permKey="inventory"><Lazy><ErrorBoundary><ProductIntelligencePage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="reliability-scores" element={<RBACRoute permKey="settings"><Lazy><ErrorBoundary><ReliabilityScoresPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="ai-insights" element={<RBACRoute permKey="dashboard"><Lazy><ErrorBoundary><AIInsightsPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="brain" element={<RBACRoute permKey="apteka_control"><Lazy><ErrorBoundary><BrainPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="exceptions" element={<RBACRoute permKey="dashboard"><Lazy><ErrorBoundary><ExceptionDashboardPage /></ErrorBoundary></Lazy></RBACRoute>} />
        <Route path="connections" element={<Lazy><ErrorBoundary><ConnectionsPage /></ErrorBoundary></Lazy>} />
        <Route path="autopilot" element={<Lazy><ErrorBoundary><AutopilotPage /></ErrorBoundary></Lazy>} />
        <Route path="subscription" element={<Lazy><ErrorBoundary><SubscriptionPage /></ErrorBoundary></Lazy>} />
        <Route path="subscription/success" element={<Lazy><ErrorBoundary><SubscriptionSuccessPage /></ErrorBoundary></Lazy>} />
        <Route path="help" element={<Lazy><ErrorBoundary><HelpPage /></ErrorBoundary></Lazy>} />
        <Route path="internal/control-room" element={<PlatformRoute><Lazy><ErrorBoundary><ControlRoomPage /></ErrorBoundary></Lazy></PlatformRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem themes={["light", "dark", "corporate", "mint", "ocean", "system"]}>
      <LanguageProvider>
        <AuthProvider>
          <PermissionsProvider>
            <PharmacyModeProvider>
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
              <Toaster position="top-right" richColors />
            </PharmacyModeProvider>
          </PermissionsProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
